import Cookies from '../services/cookies';
import {
    COOKIE_NAME_IS_WEBVIEW,
    COOKIE_NAME_SITE_CHOICE,
    COOKIE_NAME_CURRENCY,
    COOKIE_NAME_SITE_ID,
    COOKIE_NAME_SITE_ID_OLD,
    COOKIE_PAGE_VISITS_COUNT,
    COOKIE_INTERSTITIAL_DEFERRED,
    COOKIE_NAME_COUNTRY
} from '../constants';
import { debug, error as logError } from './logger';

export function chooseSiteId(currentSiteId, chosenSiteId) {
    const chosenSiteValue = `${currentSiteId}->${chosenSiteId}`;

    Cookies.setCookie(COOKIE_NAME_SITE_CHOICE, chosenSiteValue);
}

export function isWebview() {
    return !!(Cookies.getCookieByName(COOKIE_NAME_IS_WEBVIEW)) && Cookies.getCookieByName(COOKIE_NAME_IS_WEBVIEW) === 'true';
}

export function isCurrencyAvailable(state, currency) {
    const coreCurrency = _.get(state, 'core.currency');
    const currentSiteId = _.get(state, 'core.siteId');

    if (coreCurrency === currency) {
        debug(`core.currency is the same as cookie currency: ${currency}`);
        return true;
    }

    const siteWithSiteId = _.find(_.get(state, 'core.siteConfig.sites', []), (site) => _.get(site, 'siteId') === currentSiteId);

    if (siteWithSiteId) {
        const defaultTransactionalCurrency = _.get(siteWithSiteId, 'defaultCountry.defaultTransactionalCurrency');
        const convenienceCurrencies = _.map(_.get(siteWithSiteId, 'defaultCountry.convenienceCurrencies', []), 'currencyCode');

        debug(`cookie currency: ${currency}`);
        debug(`default transactional currency: ${defaultTransactionalCurrency}`);
        debug(`index of ${currency} in convenience currencies: ${convenienceCurrencies.indexOf(currency)}`);

        return currency === defaultTransactionalCurrency || convenienceCurrencies.indexOf(currency) !== -1;
    }

    logError(`could not find site in siteConfig with id: ${currentSiteId}`);
    return false;
}

export function correctCookies(state) {
    const currentSiteId = _.get(state, 'core.siteId');
    const currencyCookie = Cookies.getCookieByName(COOKIE_NAME_CURRENCY);
    const countryCookie = Cookies.getCookieByName(COOKIE_NAME_COUNTRY);
    let currency = currencyCookie;

    if (currentSiteId) {
        debug(`Setting site id cookies to ${currentSiteId}`);
        Cookies.setCookie(COOKIE_NAME_SITE_ID, currentSiteId);
        Cookies.setCookie(COOKIE_NAME_SITE_ID_OLD, currentSiteId);
    }

    if (!currencyCookie || !isCurrencyAvailable(state, currencyCookie)) {
        currency = _.get(state, 'core.currency');

        debug(`Setting currency cookie to ${currency}`);
        Cookies.setCookie(COOKIE_NAME_CURRENCY, currency);
    }
}

/**
 * Retrieve visit count from cookie
 * Unescape and parse the cookie object string
 * @returns {number} Cookie value or 0 if cookie isn't set yet
 */
export function getUniqueVisitsFromCookie() {
    const siteId = Cookies.getCookieByName(COOKIE_NAME_SITE_ID);
    const cookieObj = JSON.parse(unescape(Cookies.getCookieByName(COOKIE_PAGE_VISITS_COUNT) || '{}'));
    const value = _.get(cookieObj, `${siteId}`, 0);

    return parseInt(value, 10);
}

/**
 * Add 1 to the cookie value for current page visit count
 * Cookie is object string with count per site id
 * Unescape and parse the cookie object string
 * Escape the new cookie object string
 */
export function incrementUniquePageVisitsCount() {
    const siteId = Cookies.getCookieByName(COOKIE_NAME_SITE_ID);
    const pageVisitsCount = getUniqueVisitsFromCookie(siteId);
    const newVisitCount = (parseInt(pageVisitsCount, 10) + 1);
    const cookieObj = JSON.parse(unescape(Cookies.getCookieByName(COOKIE_PAGE_VISITS_COUNT) || '{}'));

    cookieObj[siteId] = newVisitCount;

    Cookies.setCookie(COOKIE_PAGE_VISITS_COUNT, escape(JSON.stringify(cookieObj)));
}

/**
 * Deletes existing page visit count cookie
 */
export function clearUniqueVisitsCookie() {
    Cookies.deleteCookie(COOKIE_PAGE_VISITS_COUNT);
}

/**
 * Deletes existing interstitial deferred cookie
 */
export function clearInterstitialDeferredCookie() {
    Cookies.deleteCookie(COOKIE_INTERSTITIAL_DEFERRED);
}

/**
 * Set urbn_country cookie to the default country code of the requested siteId (if it exists in the given siteConfig).
 *
 * If the countryCode cannot be found, country cookie will not be modified.
 *
 * @param {String} siteId     The site id whose default country code is to be used to update the urbn_country cookie
 * @param {Object} siteConfig The site config from core.siteConfig in the redux store
 * @return {String} the country code, if country cookie was set, undefined otherwise
 */
export function setCountryCookieToDefaultCountryCodeOfSiteId(siteId, siteConfig) {
    function siteMatchesSiteId(site) {
        return siteId === _.get(site, 'siteId');
    }

    const matchingSite = _.find(_.get(siteConfig, 'sites', []), siteMatchesSiteId);

    if (matchingSite) {
        const defaultCountryCode = _.get(matchingSite, 'defaultCountry.countryCode');

        if (defaultCountryCode) {
            Cookies.setCookie(COOKIE_NAME_COUNTRY, defaultCountryCode);
            return defaultCountryCode;
        }
    }
}
